"use client";

import { useCallback, useEffect, useState } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  Clock,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "@workos-inc/authkit-react";
import api from "../http";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router";

interface MeetingNote {
  id: string;
  title: string;
  template_id: string | null;
  generated_note_preview?: string;
  audio_duration: number | null;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

interface MeetingTemplate {
  id: string;
  name: string;
}

export default function MeetingNotes() {
  const [meetingNotes, setMeetingNotes] = useState<MeetingNote[]>([]);
  const [templates, setTemplates] = useState<MeetingTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotes, setTotalNotes] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const pageSize = 10;

  // Current user state
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);

  // Profile modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Delete confirmation state
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  // Load current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };
    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  // Load templates for display
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const token = await getAccessToken();
        const data = await api.getMeetingTemplates(token);
        setTemplates(data.templates || []);
      } catch (error) {
        console.error("Failed to load templates:", error);
      }
    };
    loadTemplates();
  }, [getAccessToken]);

  // Load meeting notes
  const loadMeetingNotes = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const data = await api.getMeetingNotes(
        currentPage,
        pageSize,
        searchFilter || undefined,
        token
      );
      setMeetingNotes(data.meeting_notes || []);
      setTotalNotes(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / pageSize));
    } catch (error) {
      console.error("Failed to load meeting notes:", error);
      toast({
        title: "Fehler",
        description: "Meetingnotizen konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken, currentPage, searchFilter, toast]);

  useEffect(() => {
    loadMeetingNotes();
  }, [loadMeetingNotes]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length >= 3 || searchInput.length === 0) {
        setSearchFilter(searchInput);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Navigate to note detail page
  const handleViewNote = (noteId: string) => {
    navigate(`/meeting-notes/${noteId}`);
  };

  // Delete note
  const deleteNote = async (noteId: string) => {
    setIsDeleting(true);
    try {
      const token = await getAccessToken();
      await api.deleteMeetingNote(noteId, token);
      toast({
        title: "Erfolg",
        description: "Meetingnotiz wurde gelöscht.",
      });
      setNoteToDelete(null);
      loadMeetingNotes();
    } catch (error) {
      console.error("Failed to delete note:", error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Sort handler
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Get template name
  const getTemplateName = (templateId: string | null) => {
    if (!templateId) return "-";
    const template = templates.find((t) => t.id === templateId);
    return template?.name || "-";
  };

  // Handle profile image update
  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  // Pagination helpers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden on mobile */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold">Meetingnotizen</h1>
          <div className="flex items-center gap-2">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto">
          {/* Mobile Header */}
          <div className="lg:hidden mb-6">
            <h1 className="text-xl font-semibold mb-4">Meetingnotizen</h1>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-medium">Alle Notizen</span>
              <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                <span
                  className="font-bold text-xs"
                  style={{ color: APP_COLOR }}
                >
                  {totalNotes} {totalNotes === 1 ? "Notiz" : "Notizen"}
                </span>
              </div>
            </div>
            {/* Mobile Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Suchen..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <span className="text-lg font-medium">Alle Meetingnotizen</span>
              <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                <span
                  className="font-bold text-xs"
                  style={{ color: APP_COLOR }}
                >
                  {totalNotes} {totalNotes === 1 ? "Notiz" : "Notizen"}
                </span>
              </div>
            </div>
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Suchen..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            )}

            {!isLoading && meetingNotes.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mb-4 text-gray-300" />
                <p className="text-lg font-medium">Keine Meetingnotizen</p>
                <p className="text-sm mt-1">Erstellen Sie Ihr erstes Meeting</p>
              </div>
            )}

            {!isLoading &&
              meetingNotes.map((note) => (
                <div
                  key={note.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:shadow-sm transition-shadow"
                  onClick={() => handleViewNote(note.id)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-gray-900">{note.title}</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setNoteToDelete(note.id);
                      }}
                      className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                    {note.generated_note_preview}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(note.audio_duration)}
                    </span>
                    <span>
                      {new Date(note.created_at).toLocaleDateString("de-DE", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              ))}

            {/* Mobile Pagination */}
            {!isLoading && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 bg-white border-none"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 bg-white border-none"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Desktop Table Container */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              {/* Table Header */}
              <div className="grid grid-cols-[3fr_1.5fr_1fr_2fr_80px] bg-gray-50 text-gray-700 border-b border-gray-200 min-w-[700px]">
                <div className="p-4 font-medium">Titel</div>
                <div className="p-4 font-medium">Vorlage</div>
                <div className="p-4 font-medium">Dauer</div>
                <div className="p-4 font-medium flex items-center gap-1">
                  Erstellt am
                  <div className="flex flex-col">
                    <div
                      className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0"
                      onClick={() => handleSort("date", "asc")}
                    >
                      <ChevronUp
                        size={14}
                        className={
                          sortField === "date" && sortDirection === "asc"
                            ? ""
                            : "text-gray-400"
                        }
                        style={
                          sortField === "date" && sortDirection === "asc"
                            ? { color: APP_COLOR }
                            : undefined
                        }
                      />
                    </div>
                    <div
                      className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0"
                      onClick={() => handleSort("date", "desc")}
                    >
                      <ChevronDown
                        size={14}
                        className={
                          sortField === "date" && sortDirection === "desc"
                            ? ""
                            : "text-gray-400"
                        }
                        style={
                          sortField === "date" && sortDirection === "desc"
                            ? { color: APP_COLOR }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="p-4 font-medium">Aktionen</div>
              </div>

              {/* Table Rows */}
              {isLoading ? (
                <div className="p-8 text-center text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                  Meetingnotizen werden geladen...
                </div>
              ) : meetingNotes.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mb-4 text-gray-300 mx-auto" />
                  <p className="text-lg font-medium">Keine Meetingnotizen gefunden</p>
                  <p className="text-sm mt-1">Erstellen Sie Ihr erstes Meeting</p>
                </div>
              ) : (
                meetingNotes.map((note) => (
                  <div
                    key={note.id}
                    className="grid grid-cols-[3fr_1.5fr_1fr_2fr_80px] border-b border-gray-200 hover:bg-gray-50 cursor-pointer min-w-[700px]"
                    onClick={() => handleViewNote(note.id)}
                  >
                    <div className="p-4 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {note.title}
                      </div>
                      <div className="text-sm text-gray-500 truncate mt-1">
                        {note.generated_note_preview || "-"}
                      </div>
                    </div>
                    <div className="p-4 flex items-center min-w-0">
                      <span className="text-gray-700 truncate">
                        {getTemplateName(note.template_id)}
                      </span>
                    </div>
                    <div className="p-4 flex items-center">
                      <span className="text-gray-700">
                        {formatDuration(note.audio_duration)}
                      </span>
                    </div>
                    <div className="p-4 flex items-center min-w-0">
                      <span className="text-gray-600 truncate">
                        {formatDate(note.created_at)}
                      </span>
                    </div>
                    <div className="p-4 flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setNoteToDelete(note.id);
                        }}
                        className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors"
                      >
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {!isLoading && totalPages > 1 && (
              <div className="flex items-center justify-between p-4 bg-gray-50 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Seite {currentPage} von {totalPages} ({totalNotes} Einträge)
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed bg-white border-none"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {getPageNumbers().map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors bg-white border-none ${
                        page === currentPage
                          ? "text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      style={
                        page === currentPage ? { backgroundColor: APP_COLOR } : {}
                      }
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed bg-white border-none"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {noteToDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Meetingnotiz löschen?
            </h2>
            <p className="text-gray-600 mb-6">
              Sind Sie sicher, dass Sie diese Meetingnotiz löschen möchten? Diese
              Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setNoteToDelete(null)}
                disabled={isDeleting}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gray-100 text-gray-700 hover:bg-gray-200 h-10 px-4 py-2"
              >
                Abbrechen
              </button>
              <button
                onClick={() => deleteNote(noteToDelete)}
                disabled={isDeleting}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white bg-red-600 hover:bg-red-700 h-10 px-4 py-2"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}
